import { ODataQueryOptions } from '@app/core/services/odata/interfaces/odata-query-options.interface';
import { PaginationOptions } from '@app/core/services/odata/interfaces/odata-paginate-options.interface';
import { ODataFilterBuilder } from '@app/core/services/odata/builders/odata-filter-builter.class';
import { SortParam } from '@app/core/services/odata/interfaces/odata-sort-param';

/**
 * Query builder for constructing OData query options
 *
 * @example
 * const query = new ODataQueryBuilder()
 *   .filter(new ODataFilterBuilder().equals('status', 'Active'))
 *   .orderBy('createdDate desc')
 *   .paginate(0, 50)
 *   .count();
 */
export class ODataQueryBuilder {
  private readonly options: ODataQueryOptions = {};

  /**
   * Set filter (can be a string or filter builder)
   *
   * @param filter - OData filter string or filter builder instance
   * @returns The query builder instance for chaining
   *
   * @example
   * // Using filter builder
   * const filterBuilder = new ODataFilterBuilder().equals('status', 'Active');
   * query.filter(filterBuilder)
   *
   * @example
   * // Using raw filter string
   * query.filter("status eq 'Active'")
   */
  public filter(filter: string | ODataFilterBuilder): this {
    if (filter instanceof ODataFilterBuilder) {
      this.options.$filter = filter.isEmpty() ? undefined : filter.build();
    } else {
      this.options.$filter = filter;
    }
    return this;
  }

  /**
   * Set order by
   *
   * @param orderBy - OData orderby expression
   * @returns The query builder instance for chaining
   *
   * @example
   * // Single field ascending
   * query.orderBy('name')
   *
   * @example
   * // Single field descending
   * query.orderBy('createdDate desc')
   *
   * @example
   * // Multiple fields
   * query.orderBy('status, createdDate desc')
   */
  public orderBy(orderBy: string): this {
    this.options.$orderby = orderBy;
    return this;
  }

  /**
   * Conditionally add orderBy
   *
   * @param sort - Sorting options
   * @returns The query builder instance for chaining
   *
   * @example
   * query.orderByIf(sort !== undefined, `${sort.field} ${sort.direction}`)
   */
  public orderByIf(sort?: SortParam): this {
    if (sort) {
      this.orderBy(`${sort.sortField} ${sort.sortDirection}`);
    }
    return this;
  }

  /**
   * Set top (limit)
   *
   * @param top - Maximum number of records to return
   * @returns The query builder instance for chaining
   *
   * @example
   * query.top(50)
   * // Returns first 50 records
   */
  public top(top: number): this {
    this.options.$top = top;
    return this;
  }

  /**
   * Set skip (offset)
   *
   * @param skip - Number of records to skip
   * @returns The query builder instance for chaining
   *
   * @example
   * query.skip(100)
   * // Skips first 100 records
   */
  public skip(skip: number): this {
    this.options.$skip = skip;
    return this;
  }

  /**
   * Set select (field projection)
   *
   * @param fields - Field names to include in the response
   * @returns The query builder instance for chaining
   *
   * @example
   * query.select('id', 'name', 'status')
   * // Returns only id, name, and status fields
   */
  public select(...fields: string[]): this {
    this.options.$select = fields;
    return this;
  }

  /**
   * Set expand (related entities)
   *
   * @param relations - Related entity names to expand/include
   * @returns The query builder instance for chaining
   *
   * @example
   * query.expand('meteringPoint', 'transactions')
   * // Includes related meteringPoint and transactions data
   */
  public expand(...relations: string[]): this {
    this.options.$expand = relations;
    return this;
  }

  /**
   * Set count
   *
   * @param includeCount - Whether to include total count in response
   * @returns The query builder instance for chaining
   *
   * @example
   * query.count()
   * // Response includes @odata.count with total record count
   */
  public count(includeCount: boolean = true): this {
    this.options.$count = includeCount;
    return this;
  }

  /**
   * Set pagination (helper for top + skip)
   *
   * @param page - Page number (zero-based)
   * @param pageSize - Number of records per page
   * @returns The query builder instance for chaining
   *
   * @example
   * // Get page 2 with 50 records per page
   * query.paginate(1, 50)
   * // Equivalent to: top(50).skip(50)
   *
   * @example
   * // Get first page with 25 records
   * query.paginate(0, 25)
   * // Equivalent to: top(25).skip(0)
   */
  public paginate(page: number, pageSize: number): this {
    this.options.$top = pageSize;
    this.options.$skip = (page - 1) * pageSize;
    return this;
  }

  /**
   * Set pagination with optional sorting in one call
   *
   * @param options - Pagination and optional sort options
   * @returns The query builder instance for chaining
   *
   * @example
   * query.paginateAndSort({
   *   page: 1,
   *   pageSize: 50,
   *   sort: { sortField: 'name', sortDirection: 'desc' }
   * })
   */
  public paginateAndSort(options: PaginationOptions): this {
    this.paginate(options.page, options.pageSize);
    if (options.sort) {
      this.orderBy(`${options.sort.sortField} ${options.sort.sortDirection}`);
    }
    return this;
  }

  /**
   * Add a custom query parameter (non-OData)
   *
   * @param key - The query parameter key
   * @param value - The query parameter value
   * @returns The query builder instance for chaining
   *
   * @example
   * query.param('language', 'da')
   *
   * @example
   * query.param('includeDeleted', 'true')
   */
  public param(key: string, value: string): this {
    if (!this.options.customParams) {
      this.options.customParams = {};
    }
    this.options.customParams[key] = value;
    return this;
  }

  /**
   * Build the final query options
   *
   * @returns The complete OData query options object
   *
   * @example
   * const query = new ODataQueryBuilder()
   *   .filter("status eq 'Active'")
   *   .orderBy('name')
   *   .top(50);
   *
   * const options = query.build();
   * // Result: { $filter: "status eq 'Active'", $orderby: 'name', $top: 50 }
   */
  public build(): ODataQueryOptions {
    return this.options;
  }
}
